import React from 'react';
import { notifyBrokerageDataChanged } from 'Components/Banking/brokerageDataBus';
import './Brokerage.css';

// Order-Formular fuer IB (Chat vom 2026-08-07, Phase 2 der IBKR-Integration).
// Kein Login/TAN-Dialog wie bei BrokerageOrderForm.js (Comdirect) - IB
// braucht keine SCA pro Order (siehe Plan "sleepy-whistling-waterfall").
// Stattdessen ein einfacher lokaler Bestaetigungsschritt (zwei Klicks statt
// einem), bevor tatsaechlich gesendet wird - kein Server-Roundtrip fuer eine
// "Challenge", da es keine gibt.
//
// props: x, target ({conId, symbol, secType, exchange, currency, action:
// 'BUY'|'SELL', orderId? (nur beim Aendern), label? (voller Name fuer die
// Ueberschrift, sonst Symbol), price? (aktueller Kurs, fuer die
// Limit-Vorbelegung)}), onDone(), onCancel().
export default function IbBrokerageOrderForm(props) {
	const x = props.x;
	const target = props.target;
	const isChange = target.orderId != null;

	// Chat vom 2026-08-07 (Nutzerwunsch: "sowohl Kauf als auch Verkauf
	// moeglich, da IB Leerverkaeufe zulaesst") - target.action ist nur noch
	// der VORSCHLAG, mit dem der Dialog geoeffnet wurde (z.B. "Kaufen" beim
	// Klick auf den IB-Kauf-Button in der Watchlist), aendert sich aber
	// selbst nicht mehr - die tatsaechlich gesendete Order-Richtung kommt
	// jetzt aus diesem eigenen State (Radiobuttons unten), unabhaengig
	// davon, wie der Dialog geoeffnet wurde. Beim Aendern einer bestehenden
	// Order (isChange) bleibt die ursspruengliche Richtung der Order
	// unveraendert waehlbar - IB erlaubt kein Umdrehen der Richtung per
	// "Change", nur neue Order.
	const [action, setAction] = React.useState(target.action || 'BUY');
	const [orderType, setOrderType] = React.useState('MKT');
	// Chat vom 2026-08-07 (Nutzerwunsch) - sinnvolle Stueckzahl-Vorbelegung
	// statt eines leeren Feldes: bei Futures ist 1 Kontrakt der ueblichere
	// Einstieg (ein Kontrakt hat bereits einen Hebel/Multiplikator), bei
	// Aktien 100 Stueck (glatte, gaengige Positionsgroesse). Nur als
	// Vorschlag - bleibt frei editierbar. Beim Aendern einer bestehenden
	// Order (target.totalQuantity gesetzt) hat die tatsaechliche Order immer
	// Vorrang.
	const [quantity, setQuantity] = React.useState(target.totalQuantity || (target.secType === 'FUT' ? 1 : 100));
	// Limit-Vorbelegung mit dem aktuellen Kurs (Nutzerwunsch) - erspart das
	// manuelle Abtippen des gerade angezeigten Kurses als Ausgangspunkt,
	// bleibt aber frei editierbar. Nur gerundet auf 2 Nachkommastellen (wie
	// die uebrige Kursanzeige), IB rundet ohnehin auf die zulaessige
	// Tick-Groesse.
	const [limitPrice, setLimitPrice] = React.useState(target.limitPrice || (target.price != null ? Math.round(target.price * 100) / 100 : ''));
	const [confirming, setConfirming] = React.useState(false);
	const [sending, setSending] = React.useState(false);
	const [error, setError] = React.useState('');
	const [result, setResult] = React.useState(null);
	const limitInputRef = React.useRef(null);
	// Chat vom 2026-08-09 (Nutzerwunsch): eine abgesendete Order soll nach
	// ~2s automatisch nochmal auf ihren Status geprueft werden. Die
	// synchrone Antwort von IbBrokeragePlaceOrder liefert nur den ERSTEN
	// orderStatus (oft "PreSubmitted"/"Pending") - die schnellen Folge-
	// wechsel (Submitted/Filled) treffen wenige Sekundenbruchteile spaeter
	// als async orderStatus-Events ein und werden serverseitig in
	// ibBrokerageOrders gespiegelt. Timer-Handle, um den Nachschlag beim
	// Schliessen des Dialogs zuverlaessig abzubrechen (kein setState nach
	// Unmount).
	const recheckTimer = React.useRef(null);
	React.useEffect(() => {
		return () => {
			if (recheckTimer.current) {
				clearTimeout(recheckTimer.current);
			}
		};
	}, []);

	// Chat vom 2026-08-07 (Nutzerwunsch) - das Limit-Feld erscheint erst,
	// wenn "Limit" als Ordertyp gewaehlt wird, und ist dann schon mit dem
	// aktuellen Kurs vorbelegt (siehe oben) - der Inhalt soll dabei markiert
	// sein, damit ein abweichender Wert direkt durch Ueberschreiben (statt
	// erst Loeschen) eingegeben werden kann.
	React.useEffect(() => {
		if (orderType === 'LMT' && limitInputRef.current) {
			limitInputRef.current.select();
		}
	}, [orderType]);

	function submit() {
		if (!confirming) {
			setConfirming(true);
			return;
		}
		setSending(true);
		setError('');
		const task = isChange ? 'IbBrokerageChangeOrder' : 'IbBrokeragePlaceOrder';
		const body = {
			conId: target.conId,
			symbol: target.symbol,
			secType: target.secType,
			exchange: target.exchange,
			currency: target.currency,
			action,
			orderType,
			totalQuantity: quantity,
			limitPrice: orderType === 'LMT' ? limitPrice : undefined,
		};
		if (isChange) {
			body.orderId = target.orderId;
		}
		x.was.axPost('Backend/webapp.php?module=IbBrokerage&task=' + task, body, (res) => {
			setSending(false);
			const content = res.data.content || {};
			if (res.data.returncode !== 1) {
				setError(content.error || 'Order fehlgeschlagen - IB-Trading ist vermutlich nicht freigeschaltet.');
				return;
			}
			if (content.error) {
				setError(content.error);
				return;
			}
			setResult(content);
			notifyBrokerageDataChanged(x);
			// Nachschlag nach 2s (siehe recheckTimer oben): den inzwischen
			// gespiegelten, aktuelleren Status der gerade gesendeten Order
			// holen und im Ergebnis-Screen anzeigen sowie Orderbuch/Depot
			// erneut auffrischen. Ueber die orderId aus der Sofort-Antwort.
			if (content.orderId != null) {
				recheckTimer.current = setTimeout(() => {
					recheckOrderStatus(content.orderId);
				}, 2000);
			}
		});
	}

	function recheckOrderStatus(orderId) {
		x.was.axPost('Backend/webapp.php?module=IbBrokerage&task=IbBrokerageOrderList', {}, (res) => {
			if (res.data.returncode !== 1) {
				return;
			}
			const orders = res.data.content.orders || [];
			const match = orders.find((o) => String(o.orderid) === String(orderId));
			if (match && match.orderstatus) {
				// Nur den Status im bereits angezeigten Ergebnis aktualisieren,
				// Rest (orderId/note) unveraendert lassen.
				setResult((prev) => (prev ? { ...prev, status: match.orderstatus } : prev));
			}
			// Orderbuch/Depot ebenfalls auffrischen - der Status kann sich seit
			// dem ersten notify (direkt nach dem Senden) geaendert haben.
			notifyBrokerageDataChanged(x);
		});
	}

	if (result) {
		return (
			<div className="brokerage-card">
				{props.onDone && <i className="bi bi-x-lg brokerage-card-close" title="Schließen" onClick={props.onDone}></i>}
				<h4>{isChange ? 'Order geändert' : 'Order gesendet'}</h4>
				<p>Order-ID {result.orderId} – Status: {result.status || 'Pending'}</p>
				{result.note && <p className="brokerage-hint">{result.note}</p>}
				<button type="button" className="btn btn-primary" onClick={() => props.onDone && props.onDone()}>Schließen</button>
			</div>
		);
	}

	return (
		<div className="brokerage-card">
			{props.onCancel && <i className="bi bi-x-lg brokerage-card-close" title="Schließen" onClick={props.onCancel}></i>}
			<h4>{isChange ? 'Order ändern' : (action === 'SELL' ? 'Verkaufen' : 'Kaufen')}: {target.label || target.symbol}</h4>
			{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
			{/* Nicht ueber "brokerage-field-row" (display:contents auf Desktop,
			    siehe Brokerage.css) - die beiden Radiobuttons sollen immer in
			    EINER Zeile nebeneinander stehen, nicht wie Label/Feld gestapelt
			    werden. */}
			<div className="brokerage-action-row">
				<label className="brokerage-radio">
					<input type="radio" name="ib-order-action" value="BUY" checked={action === 'BUY'} onChange={() => setAction('BUY')} />
					Kaufen
				</label>
				<label className="brokerage-radio">
					<input type="radio" name="ib-order-action" value="SELL" checked={action === 'SELL'} onChange={() => setAction('SELL')} />
					Verkaufen (Leerverkauf möglich)
				</label>
			</div>
			{/* Chat vom 2026-08-07 (Nutzerwunsch: "kann um jede Sekunde gehen") -
			    Radiobuttons statt Dropdown, analog Kaufen/Verkaufen oben - ein
			    Klick statt Dropdown-oeffnen-dann-Option-treffen. */}
			<div className="brokerage-action-row">
				<label className="brokerage-radio">
					<input type="radio" name="ib-order-type" value="MKT" checked={orderType === 'MKT'} onChange={() => setOrderType('MKT')} />
					Market
				</label>
				<label className="brokerage-radio">
					<input type="radio" name="ib-order-type" value="LMT" checked={orderType === 'LMT'} onChange={() => setOrderType('LMT')} />
					Limit
				</label>
			</div>
			<div className="brokerage-field-row">
				<label>Stück</label>
				<input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
			</div>
			{orderType === 'LMT' &&
				<div className="brokerage-field-row">
					<label>Limit</label>
					{/* step="1" (Nutzerwunsch) - die Pfeiltasten des Zahlenfelds
					    veraendern den Kurs in ganzen Einer-Schritten vor dem Komma,
					    Nachkommastellen bleiben trotzdem frei eintippbar (kein "step"-
					    Rundungszwang bei manueller Eingabe, nur bei den Pfeilen). */}
					<input ref={limitInputRef} type="number" step="1" value={limitPrice} onChange={(e) => setLimitPrice(e.target.value)} />
				</div>
			}
			{confirming &&
				<p className="brokerage-hint">
					<i className="bi bi-exclamation-triangle"></i> Diese Order wird bei Bestätigung SOFORT und OHNE weitere Rückfrage bei IB ausgeführt.
				</p>
			}
			<div className="brokerage-order-detail-actions">
				<button type="button" className="btn btn-secondary" onClick={() => props.onCancel && props.onCancel()}>Abbrechen</button>
				<button type="button" className="btn btn-primary" disabled={sending || !quantity} onClick={submit}>
					{sending ? 'Sende…' : (confirming ? 'Jetzt verbindlich senden' : 'Weiter')}
				</button>
			</div>
		</div>
	);
}
